import { createMapLibreOverlay } from '@mappedin/maplibre-overlay';
import type { MappedinMapLibreOverlay } from '@mappedin/maplibre-overlay';
import { Coordinate, hydrateMapData } from '@mappedin/mappedin-js';
import type { MapView } from '@mappedin/mappedin-js';
import type { VenueOSClient } from '@mappedin/venueos-applet';
import maplibregl from 'maplibre-gl';
import type { Venue } from '../types';
import type {
  MappedinFloor,
  MappedinLocation,
  MappedinMapData,
  MappedinMapView,
  MappedinNode,
} from './mappedin-types';

/** Default zoom level when centering the camera on a venue. */
const DEFAULT_VENUE_ZOOM = 15;

/** Zoom level used when focusing on a single transit stop. */
const STOP_FOCUS_ZOOM = 17;

/** Camera animation duration when recentering or focusing. */
const CAMERA_ANIMATION_MS = 600;

/** Inset around the map container so markers near the edge stay visible. */
const MAP_SAFE_INSET_RATIO = 0.05;

/** OpenFreeMap fallback if Mappedin tiles config isn't reachable. */
const FALLBACK_STYLE = 'https://tiles.openfreemap.org/styles/positron';

const ENTRANCE_PIN_HTML =
  '<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-50%);">' +
  '<div style="width:32px;height:32px;border-radius:50%;background:#4990f3;border:2.5px solid #fff;box-shadow:0 2px 10px rgba(73,144,243,0.5);display:flex;align-items:center;justify-content:center;">' +
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>' +
  '</svg></div>' +
  '<div style="width:2px;height:6px;background:#4990f3;"></div>' +
  '<div style="width:6px;height:6px;border-radius:50%;background:#4990f3;box-shadow:0 0 6px rgba(73,144,243,0.6);"></div>' +
  '</div>';

interface LoadVenueMapResult {
  mapView: MappedinMapView;
  mapData: MappedinMapData;
  mapLibreMap: maplibregl.Map;
  overlay: MappedinMapLibreOverlay;
}

interface TilesConfig {
  key: string;
  styleUrl: string;
}

/** Construct a Mappedin Coordinate from raw lat/lng. */
function coordinate(latitude: number, longitude: number): Coordinate {
  return new Coordinate({ latitude, longitude });
}

/**
 * Fetch the Mappedin tile style JSON ahead of time, with the auth header.
 *
 * MapLibre's `transformRequest` is invoked on tile requests but the
 * initial `style.json` fetch races the map constructor; by pre-fetching it
 * ourselves we guarantee MapLibre receives a valid style spec instead of a
 * 401 from the CDN.
 */
async function fetchStyleJson(tiles: TilesConfig): Promise<maplibregl.StyleSpecification | null> {
  try {
    const res = await fetch(tiles.styleUrl, {
      headers: { 'x-mappedin-tiles-key': tiles.key },
    });
    if (!res.ok) return null;
    return (await res.json()) as maplibregl.StyleSpecification;
  } catch {
    return null;
  }
}

async function resolveTiles(
  client: VenueOSClient,
  mapInfoTiles: TilesConfig | null,
): Promise<TilesConfig | null> {
  if (mapInfoTiles) return mapInfoTiles;
  try {
    return await client.getTilesConfig();
  } catch {
    return null;
  }
}

/**
 * Render the venue's outdoor + indoor map using web-applet's pattern:
 *
 *   1. Create a MapLibre map authenticated with the Mappedin tile key (via
 *      both the pre-fetched style JSON and a `transformRequest` that adds
 *      the `x-mappedin-tiles-key` header to every CDN request). The
 *      MapLibre map owns the camera and renders the outdoor basemap.
 *   2. Hydrate the venue's MVF binary into Mappedin `MapData`.
 *   3. Add the Mappedin `MapView` as an overlay control on top of MapLibre
 *      via `createMapLibreOverlay`. This is the inverse of `show3dMap`'s
 *      embedded outdoor mode and is the same approach web-applet uses.
 *   4. Inside the overlay's `loaded` event we apply the multi-floor
 *      footprint, hide indoor geometry, and drop entrance pins.
 *
 * Returns both the MapLibre map (used by `TransitMarkerLayer` for
 * clustering) and the Mappedin `MapView` (used for placing 3D markers).
 */
export async function loadVenueMap(
  client: VenueOSClient,
  container: HTMLElement,
  venue: Venue,
): Promise<LoadVenueMapResult> {
  const mapInfo = await client.getMapData({ mapId: venue.id });
  const tiles = await resolveTiles(client, mapInfo.tiles ?? null);

  let style: maplibregl.StyleSpecification | string = FALLBACK_STYLE;
  if (tiles) {
    const styleJson = await fetchStyleJson(tiles);
    if (styleJson) {
      if (!styleJson.projection) styleJson.projection = { type: 'mercator' };
      style = styleJson;
    }
  }

  const tilesKey = tiles?.key;
  const baseMapOptions: maplibregl.MapOptions = {
    container,
    style,
    center: [venue.longitude, venue.latitude],
    zoom: DEFAULT_VENUE_ZOOM,
    maxZoom: 22,
    attributionControl: false,
  };
  if (tilesKey) {
    baseMapOptions.transformRequest = (url) => {
      try {
        if (new URL(url).hostname === 'tiles-cdn.mappedin.com') {
          return { url, headers: { 'x-mappedin-tiles-key': tilesKey } };
        }
      } catch {
        // invalid URL — skip token injection
      }
      return { url };
    };
  }

  const mapLibreMap = new maplibregl.Map(baseMapOptions);
  mapLibreMap.addControl(new maplibregl.AttributionControl({ compact: true }));

  const mvfResponse = await fetch(mapInfo.mvfUrl);
  if (!mvfResponse.ok) {
    mapLibreMap.remove();
    throw new Error(`Failed to download MVF: ${mvfResponse.status}`);
  }
  const zipBuffer = new Uint8Array(await mvfResponse.arrayBuffer());

  const mapData = (await hydrateMapData({
    type: 'binary',
    main: zipBuffer,
  })) as unknown as MappedinMapData;

  if (!mapLibreMap.loaded()) {
    await new Promise<void>((resolve) => {
      void mapLibreMap.once('load', () => {
        resolve();
      });
    });
  }

  const overlay = createMapLibreOverlay(
    mapData as unknown as Parameters<typeof createMapLibreOverlay>[0],
    {
      multiFloorView: {
        enabled: true,
        floorGap: 10,
        footprintColor: '#696969',
        footprintOpacity: 1,
        updateCameraElevationOnFloorChange: true,
      },
    } as Parameters<typeof createMapLibreOverlay>[1],
  );

  mapLibreMap.addControl(overlay);

  const mapView = await new Promise<MappedinMapView>((resolve) => {
    overlay.on('loaded', ({ mapView: rawMapView }: { mapView: MapView }) => {
      resolve(rawMapView as unknown as MappedinMapView);
    });
  });

  hideIndoorGeometry(mapView, mapData);
  addEntranceMarkers(mapView, mapData, venue);
  applySafeInsets(mapView, container);
  recenterOnVenue(mapLibreMap, venue);

  return { mapView, mapData, mapLibreMap, overlay };
}

function hideIndoorGeometry(mapView: MappedinMapView, mapData: MappedinMapData): void {
  try {
    const floors = mapData.getByType('floor') as MappedinFloor[];
    let topFloor: MappedinFloor | null = null;
    for (const floor of floors) {
      if (floor.elevation == null) continue;
      if (!topFloor || floor.elevation > (topFloor.elevation ?? -Infinity)) {
        topFloor = floor;
      }
    }
    if (!topFloor) return;
    mapView.setFloor(topFloor);
    mapView.updateState(topFloor, {
      geometry: { visible: false },
      footprint: { ceilingVisible: true, visible: true },
    });
  } catch {
    // Map data may not include floors (some outdoor-only venues).
  }
}

function addEntranceMarkers(
  mapView: MappedinMapView,
  mapData: MappedinMapData,
  venue: Venue,
): void {
  try {
    const isCms = venue.id.startsWith('cms:');
    const locations = (
      isCms ? mapData.getByType('enterprise-location') : mapData.getByType('location-profile')
    ) as MappedinLocation[];

    for (const loc of locations) {
      const isEntrance =
        (loc.type === 'amenity' && /entrance/i.test(loc.amenity ?? '')) ||
        /entrance/i.test(loc.name ?? '');
      if (!isEntrance) continue;
      for (const node of loc.nodes ?? ([] as MappedinNode[])) {
        const nc = node.coordinate;
        if (!nc) continue;
        try {
          mapView.Markers.add(coordinate(nc.latitude, nc.longitude), ENTRANCE_PIN_HTML, {
            rank: 'always-visible',
          });
        } catch {
          // Skip individual marker placement failures.
        }
      }
    }
  } catch {
    // Optional polish; never fatal.
  }
}

function applySafeInsets(mapView: MappedinMapView, container: HTMLElement): void {
  try {
    const cw = container.offsetWidth || 800;
    const ch = container.offsetHeight || 600;
    const inset = Math.round(Math.min(cw, ch) * MAP_SAFE_INSET_RATIO);
    mapView.Camera.setScreenOffsets?.({
      top: inset,
      bottom: inset,
      left: inset,
      right: inset,
    });
  } catch {
    // Insets are cosmetic — ignore failures.
  }
}

/** Animate the MapLibre camera to the venue's coordinates. */
export function recenterOnVenue(map: maplibregl.Map, venue: Venue): void {
  try {
    map.flyTo({
      center: [venue.longitude, venue.latitude],
      zoom: DEFAULT_VENUE_ZOOM,
      duration: CAMERA_ANIMATION_MS,
    });
  } catch {
    // Animation can fail mid-teardown; non-fatal.
  }
}

/** Frame the camera around a single transit stop. */
export function focusOnStop(map: maplibregl.Map, latitude: number, longitude: number): void {
  try {
    map.flyTo({
      center: [longitude, latitude],
      zoom: STOP_FOCUS_ZOOM,
      duration: CAMERA_ANIMATION_MS,
    });
  } catch {
    // Animation can fail mid-teardown; non-fatal.
  }
}
